// A live preview of a game for the feed: the module's rules are pure, so the page can run a
// Room of its own with a few bots and draw it into a small canvas. No server involved, and the
// same building block Create's live preview will use. Paused while off-screen.

import { loadModule, type ClientWorld, type GameModule } from "../contract.js";
import { games } from "../../games/index.js";
import { Room } from "../server/room.js";
import { makeRng } from "../shared/rng.js";
import { Camera } from "./camera.js";
import { drawFrame } from "./render.js";
import { spriteFor } from "./sprites.js";

export interface PreviewHandle {
  /** The game's display name, from its module. */
  name: string;
  stop(): void;
  /** Pause or resume simulation and drawing (the feed toggles this as cards scroll in and out). */
  setActive(on: boolean): void;
}

const BOT_NAMES = ["ada", "ben", "cy", "dee", "eli", "fay", "gus", "hal", "ivy", "jo"];
const RENDER_INTERVAL_MS = 50;

export interface PreviewOptions {
  bots?: number;
  /** Where the "local server" label sits, in CSS pixels from the top (the feed keeps it under its pills). */
  labelTop?: number;
}

export async function mountPreview(canvas: HTMLCanvasElement, slug: string, opts: PreviewOptions = {}): Promise<PreviewHandle> {
  const entry = games[slug];
  if (!entry) throw new Error(`unknown game "${slug}"`);
  return mountPreviewModule(canvas, await loadModule(entry.load), slug, opts);
}

/** The same preview for a module already loaded, such as one the Create tab just generated. */
export function mountPreviewModule(canvas: HTMLCanvasElement, game: GameModule, slug: string, opts: PreviewOptions = {}): PreviewHandle {
  const bots = opts.bots ?? 6;
  const labelTop = opts.labelTop ?? 6;
  const room = new Room(game, { id: `preview/${slug}`, seed: hashSeed(slug) });
  const rng = makeRng(hashSeed(slug) ^ 0x5eed);
  const { w, h } = game.meta.world;

  const walkers: { pid: string; tx: number; ty: number; at: number }[] = [];
  for (let i = 0; i < Math.min(bots, game.meta.players.max); i++) {
    const pid = `b${i}`;
    room.join(pid, BOT_NAMES[i % BOT_NAMES.length]);
    walkers.push({ pid, tx: w / 2, ty: h / 2, at: 0 });
  }

  const ctx = canvas.getContext("2d")!;
  const camera = new Camera();
  camera.magnify = 2.2;
  const world: ClientWorld = {
    meta: game.meta,
    w,
    h,
    get you() {
      return room.playerEntity("b0")?.id;
    },
    get time() {
      return room.state.time;
    },
    entities: () => room.liveEntities(),
    get: (id) => room.state.entities[id],
    name: (pid) => (pid !== undefined && room.state.players[pid]?.name) || "?",
    sprite: spriteFor,
  };

  let active = true;
  let stopped = false;
  let broken: string | null = null;

  const ticker = window.setInterval(() => {
    if (!active || stopped || broken) return;
    const t = room.state.tick;
    for (const b of walkers) {
      if (t >= b.at) {
        b.tx = rng.range(0, w);
        b.ty = rng.range(0, h);
        b.at = t + 20 + rng.int(60);
      }
      room.setInput(b.pid, { tx: b.tx, ty: b.ty, a: rng.next() < 0.15, b: rng.next() < 0.15 });
    }
    try {
      room.step();
    } catch (err) {
      broken = (err as Error).message;
    }
  }, 1000 / game.meta.tickRate);

  let raf = 0;
  let lastDraw = 0;
  let lastFrame = performance.now();
  const frame = (now: number) => {
    if (stopped) return;
    raf = requestAnimationFrame(frame);
    if (!active || now - lastDraw < RENDER_INTERVAL_MS) return;
    const dt = Math.min(0.1, (now - lastFrame) / 1000);
    lastFrame = now;
    lastDraw = now;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    if (!cw || !ch) return;
    if (canvas.width !== Math.round(cw * dpr) || canvas.height !== Math.round(ch * dpr)) {
      canvas.width = Math.round(cw * dpr);
      canvas.height = Math.round(ch * dpr);
    }
    camera.follow(room.playerEntity("b0"), cw, ch, dt, w, h);
    drawFrame(ctx, cw, ch, dpr, camera, room.liveEntities(), game, world, 0);
    // This card really is a server: the room lives in this page and the bots are its clients.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.font = "11px ui-monospace, Menlo, Consolas, monospace";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    const label = `local server in this page · ${room.playerCount} bots connected`;
    const tw = ctx.measureText(label).width;
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(6, labelTop, tw + 12, 18);
    ctx.fillStyle = "#e6e9ee";
    ctx.fillText(label, 12, labelTop + 3);
    if (broken) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.fillRect(0, 0, cw, ch);
      ctx.fillStyle = "#ff8080";
      ctx.font = "12px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(`game error: ${broken}`, cw / 2, ch / 2);
    }
  };
  raf = requestAnimationFrame(frame);

  return {
    name: game.meta.name,
    stop() {
      stopped = true;
      clearInterval(ticker);
      cancelAnimationFrame(raf);
    },
    setActive(on) {
      active = on;
      if (on) lastFrame = performance.now();
    },
  };
}

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}
